// Sous-éditeurs du champ sélectionné : options d'un champ à choix, colonnes d'un tableau de
// lignes, calcul d'un champ numérique (somme ou produit de champs, constantes, tarif, colonne).
import { TYPES_COLONNE, TYPES_NUMERIQUES, type Calcul, type Champ, type Colonne, type Operande } from '@cc/contrats';
import { Bouton, CaseACocher, Champ as ChampTexte, Selection } from '@cc/ui';
import { Plus, Trash2 } from 'lucide-react';
import { genererCode } from './constructeur';
import demarches from '../demarches.module.css';
import styles from './constructeur.module.css';

type Option = { valeur: string; libelle: string };

export function EditeurOptions({ options, onChanger, titre = 'Options proposées' }: { options: Option[]; onChanger: (o: Option[]) => void; titre?: string }) {
  return (
    <fieldset className={demarches.sousEditeur}>
      <legend>{titre}</legend>
      {options.map((o, i) => (
        <div key={i} className={styles.ligneOption}>
          <ChampTexte
            libelle={`Libellé de l'option ${i + 1}`}
            value={o.libelle}
            onChange={(e) => {
              const libelle = e.target.value;
              const autres = options.filter((_, j) => j !== i).map((x) => x.valeur);
              onChanger(options.map((x, j) => (j === i ? { libelle, valeur: genererCode(libelle, autres, 'option') } : x)));
            }}
          />
          <span className={styles.codeOption} aria-label={`Valeur enregistrée de l'option ${i + 1}`}>
            {o.valeur}
          </span>
          <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer l'option ${i + 1}`} disabled={options.length <= 1} onClick={() => onChanger(options.filter((_, j) => j !== i))}>
            Retirer
          </Bouton>
        </div>
      ))}
      <div>
        <Bouton petit variante="secondaire" icone={Plus} onClick={() => onChanger([...options, { valeur: genererCode(`Option ${options.length + 1}`, options.map((x) => x.valeur), 'option'), libelle: `Option ${options.length + 1}` }])}>
          Ajouter une option
        </Bouton>
      </div>
    </fieldset>
  );
}

const LIBELLES_COLONNE: Record<string, string> = { texte_court: 'Texte', nombre: 'Nombre', montant: 'Montant', date: 'Date', choix_unique: 'Choix unique', oui_non: 'Oui ou non' };

export function EditeurColonnes({ colonnes, onChanger }: { colonnes: Colonne[]; onChanger: (c: Colonne[]) => void }) {
  const maj = (i: number, patch: Partial<Colonne>) => onChanger(colonnes.map((k, j) => (j === i ? { ...k, ...patch } : k)));
  return (
    <fieldset className={demarches.sousEditeur}>
      <legend>Colonnes du tableau</legend>
      {colonnes.map((k, i) => (
        <div key={i} className={styles.blocColonne}>
          <div className={styles.ligneOption}>
            <ChampTexte
              libelle={`Libellé de la colonne ${i + 1}`}
              value={k.libelle}
              onChange={(e) => maj(i, { libelle: e.target.value, code: genererCode(e.target.value, colonnes.filter((_, j) => j !== i).map((x) => x.code), 'colonne') })}
            />
            <Selection
              libelle="Type"
              value={k.type}
              options={TYPES_COLONNE.map((t) => ({ valeur: t, libelle: LIBELLES_COLONNE[t] ?? t }))}
              onChange={(e) => {
                const type = e.target.value as Colonne['type'];
                maj(i, { type, options: type === 'choix_unique' ? (k.options ?? [{ valeur: 'option_1', libelle: 'Option 1' }]) : undefined });
              }}
            />
            <CaseACocher libelle="Obligatoire" checked={k.obligatoire} onChange={(e) => maj(i, { obligatoire: e.target.checked })} />
            <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer la colonne ${i + 1}`} disabled={colonnes.length <= 1} onClick={() => onChanger(colonnes.filter((_, j) => j !== i))}>
              Retirer
            </Bouton>
          </div>
          {k.type === 'choix_unique' ? <EditeurOptions titre={`Options de la colonne « ${k.libelle} »`} options={k.options ?? []} onChanger={(o) => maj(i, { options: o })} /> : null}
        </div>
      ))}
      <div>
        <Bouton petit variante="secondaire" icone={Plus} onClick={() => onChanger([...colonnes, { code: genererCode(`Colonne ${colonnes.length + 1}`, colonnes.map((x) => x.code), 'colonne'), type: 'texte_court', libelle: `Colonne ${colonnes.length + 1}`, obligatoire: false }])}>
          Ajouter une colonne
        </Bouton>
      </div>
    </fieldset>
  );
}

type GenreOperande = 'champ' | 'constante' | 'tarif' | 'colonne';
const genre = (o: Operande): GenreOperande => ('champ' in o ? 'champ' : 'constante' in o ? 'constante' : 'tarif' in o ? 'tarif' : 'colonne');

export interface ProprietesEditeurCalcul {
  champ: Champ;
  calcul: Calcul | undefined;
  onChanger: (c: Calcul | undefined) => void;
  /** Autres champs du formulaire (opérandes possibles). */
  champs: Champ[];
  rubriqueTarif: string | null;
}

export function EditeurCalcul({ champ, calcul, onChanger, champs, rubriqueTarif }: ProprietesEditeurCalcul) {
  const numeriques = champs.filter((c) => c.code !== champ.code && TYPES_NUMERIQUES.includes(c.type));
  const tableaux = champs.filter((c) => c.type === 'tableau' && (c.colonnes ?? []).some((k) => k.type === 'nombre' || k.type === 'montant'));
  const operandes = calcul?.operandes ?? [];
  const majOperandes = (liste: Operande[]) => onChanger({ operation: calcul?.operation ?? 'somme', operandes: liste });
  const nouvelOperande = (g: GenreOperande): Operande => {
    if (g === 'champ') return { champ: numeriques[0]?.code ?? '' };
    if (g === 'constante') return { constante: 1 };
    if (g === 'tarif') return { tarif: rubriqueTarif ?? '' };
    const t = tableaux[0];
    const k = t?.colonnes?.find((x) => x.type === 'nombre' || x.type === 'montant');
    return { colonne: { tableau: t?.code ?? '', colonne: k?.code ?? '' } };
  };
  return (
    <fieldset className={demarches.sousEditeur}>
      <legend>Calcul automatique</legend>
      <CaseACocher libelle="Champ calculé par le serveur (la saisie de l'usager est ignorée)" checked={Boolean(calcul)} onChange={(e) => onChanger(e.target.checked ? { operation: 'somme', operandes: [nouvelOperande(numeriques.length > 0 ? 'champ' : 'constante')] } : undefined)} />
      {calcul ? (
        <>
          <Selection libelle="Opération" value={calcul.operation} options={[{ valeur: 'somme', libelle: 'Somme des opérandes' }, { valeur: 'produit', libelle: 'Produit des opérandes' }]} onChange={(e) => onChanger({ ...calcul, operation: e.target.value as Calcul['operation'] })} />
          {operandes.map((o, i) => {
            const g = genre(o);
            return (
              <div key={i} className={styles.ligneOption}>
                <Selection
                  libelle={`Opérande ${i + 1}`}
                  value={g}
                  options={[
                    { valeur: 'champ', libelle: 'Champ numérique', desactivee: numeriques.length === 0 },
                    { valeur: 'constante', libelle: 'Constante' },
                    { valeur: 'tarif', libelle: 'Tarif de la démarche', desactivee: !rubriqueTarif },
                    { valeur: 'colonne', libelle: 'Somme d\'une colonne', desactivee: tableaux.length === 0 },
                  ]}
                  onChange={(e) => majOperandes(operandes.map((x, j) => (j === i ? nouvelOperande(e.target.value as GenreOperande) : x)))}
                />
                {'champ' in o ? <Selection libelle="Champ" value={o.champ} options={numeriques.map((c) => ({ valeur: c.code, libelle: c.libelle }))} onChange={(e) => majOperandes(operandes.map((x, j) => (j === i ? { champ: e.target.value } : x)))} /> : null}
                {'constante' in o ? <ChampTexte libelle="Valeur" type="number" step="any" value={String(o.constante)} onChange={(e) => majOperandes(operandes.map((x, j) => (j === i ? { constante: Number(e.target.value) || 0 } : x)))} /> : null}
                {'tarif' in o ? <ChampTexte libelle="Rubrique" value={o.tarif} readOnly aide="Montant indicatif de l'onglet Tarif" /> : null}
                {'colonne' in o ? (
                  <>
                    <Selection libelle="Tableau" value={o.colonne.tableau} options={tableaux.map((t) => ({ valeur: t.code, libelle: t.libelle }))} onChange={(e) => majOperandes(operandes.map((x, j) => (j === i ? { colonne: { tableau: e.target.value, colonne: '' } } : x)))} />
                    <Selection
                      libelle="Colonne"
                      value={o.colonne.colonne}
                      optionVide="Choisir"
                      options={(tableaux.find((t) => t.code === o.colonne.tableau)?.colonnes ?? []).filter((k) => k.type === 'nombre' || k.type === 'montant').map((k) => ({ valeur: k.code, libelle: k.libelle }))}
                      onChange={(e) => majOperandes(operandes.map((x, j) => (j === i ? { colonne: { tableau: o.colonne.tableau, colonne: e.target.value } } : x)))}
                    />
                  </>
                ) : null}
                <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer l'opérande ${i + 1}`} disabled={operandes.length <= 1} onClick={() => majOperandes(operandes.filter((_, j) => j !== i))}>
                  Retirer
                </Bouton>
              </div>
            );
          })}
          <div>
            <Bouton petit variante="secondaire" icone={Plus} onClick={() => majOperandes([...operandes, nouvelOperande('constante')])}>
              Ajouter un opérande
            </Bouton>
          </div>
        </>
      ) : null}
    </fieldset>
  );
}
